import { useMemo } from 'react';

// 8 color palettes — cycle via (peethId-1) % 8
const P = [
  { s1:'#0c0225',s2:'#1a0840',t:'#2c1260',a:'#D4AF37',g:'#9B59B6',m:'#E8D5FF' }, // 0 purple
  { s1:'#1e0202',s2:'#380505',t:'#580808',a:'#FF9500',g:'#E74C3C',m:'#FFE0C0' }, // 1 red
  { s1:'#021e08',s2:'#053514',t:'#084820',a:'#FFD700',g:'#27AE60',m:'#D5FFE0' }, // 2 forest
  { s1:'#020a1e',s2:'#051535',t:'#082050',a:'#93C5FD',g:'#2980B9',m:'#D5EEFF' }, // 3 ocean
  { s1:'#1e1002',s2:'#352005',t:'#4a2d08',a:'#FFD700',g:'#E67E22',m:'#FFF0A0' }, // 4 desert
  { s1:'#02020f',s2:'#05051e',t:'#08082e',a:'#B0B0FF',g:'#5B2AE8',m:'#DCDEFF' }, // 5 indigo
  { s1:'#1e020c',s2:'#350518',t:'#500824',a:'#FFB0D0',g:'#E91E8C',m:'#FFD8EE' }, // 6 rose
  { s1:'#021e1e',s2:'#053535',t:'#084848',a:'#7FFFEF',g:'#16A085',m:'#D0FFF5' }, // 7 teal
];

function getTerrainType(state, country) {
  if (country && country !== 'India') return 'hills';
  if (['Uttarakhand','Himachal Pradesh','Jammu & Kashmir','PoK'].includes(state)) return 'mountain';
  if (['Tamil Nadu','Kerala'].includes(state)) return 'coastal';
  if (['Gujarat','Rajasthan'].includes(state)) return 'desert';
  if (['West Bengal','Bihar','Uttar Pradesh','Jharkhand','Odisha'].includes(state)) return 'river';
  if (['Assam','Tripura','Chhattisgarh','Andhra Pradesh','Maharashtra'].includes(state)) return 'forest';
  return 'hills';
}

function buildTerrainPath(type, W, H) {
  const ty = H * 0.575;
  switch(type) {
    case 'mountain':
      return `M0,${ty} Q${W*.12},${ty-18} ${W*.25},${ty-10} Q${W*.38},${ty-30} ${W*.5},${ty-18} Q${W*.63},${ty-34} ${W*.77},${ty-15} Q${W*.9},${ty-7} ${W},${ty-13} L${W},${H} L0,${H}Z`;
    case 'river':
      return `M0,${ty+4} Q${W*.25},${ty-7} ${W*.5},${ty+4} Q${W*.75},${ty+13} ${W},${ty+4} L${W},${H} L0,${H}Z`;
    case 'forest':
      return `M0,${ty} L${W*.08},${ty-13} L${W*.18},${ty-5} L${W*.28},${ty-17} L${W*.38},${ty-7} L${W*.5},${ty-21} L${W*.62},${ty-9} L${W*.72},${ty-16} L${W*.83},${ty-5} L${W*.92},${ty-13} L${W},${ty} L${W},${H} L0,${H}Z`;
    case 'coastal':
      return `M0,${ty+6} Q${W*.25},${ty} ${W*.5},${ty+8} Q${W*.75},${ty+15} ${W},${ty+6} L${W},${H} L0,${H}Z`;
    case 'desert':
      return `M0,${ty+5} Q${W*.2},${ty-6} ${W*.42},${ty+5} Q${W*.62},${ty+14} ${W*.8},${ty+2} Q${W*.9},${ty-4} ${W},${ty+5} L${W},${H} L0,${H}Z`;
    default: // hills
      return `M0,${ty+4} Q${W*.25},${ty-13} ${W*.5},${ty+4} Q${W*.75},${ty+17} ${W},${ty+4} L${W},${H} L0,${H}Z`;
  }
}

// Fixed star positions (relative to 120×160 viewBox)
const STARS = [
  [10,9],[38,7],[66,12],[94,6],[112,17],
  [23,25],[55,20],[86,28],[7,37],[108,30],
  [44,43],[76,38],[16,49],[100,45],[30,55],
];

const PARTS = [
  { dx:-22, delay:'0s',   dur:'3.0s' },
  { dx: 0,  delay:'1.1s', dur:'3.4s' },
  { dx: 22, delay:'0.55s',dur:'2.7s' },
  { dx:-10, delay:'1.7s', dur:'3.8s' },
];

// ── Temple silhouette components ─────────────────────────────────────────────

function NagaraTemple({ cx, by, fill, accent }) {
  return (
    <g>
      <rect x={cx-22} y={by-8} width={44} height={8} rx={1.5} fill={fill} />
      <rect x={cx-17} y={by-16} width={34} height={9} rx={1.5} fill={fill} opacity={.9} />
      <rect x={cx-12} y={by-23} width={24} height={8} rx={1} fill={fill} opacity={.85} />
      <polygon points={`${cx-11},${by-23} ${cx+11},${by-23} ${cx+5},${by-47} ${cx-5},${by-47}`} fill={fill} opacity={.9} />
      <polygon points={`${cx-5},${by-47} ${cx+5},${by-47} ${cx+2.5},${by-62} ${cx-2.5},${by-62}`} fill={fill} />
      <ellipse cx={cx} cy={by-65} rx={3.8} ry={2.5} fill={accent} opacity={.75} />
      <circle cx={cx} cy={by-69} r={2.5} fill={accent} opacity={.85} />
      <line x1={cx} y1={by-72} x2={cx} y2={by-79} stroke={accent} strokeWidth={1.3} />
      <polygon points={`${cx},${by-79} ${cx+6},${by-76} ${cx},${by-73}`} fill={accent} opacity={.75} />
    </g>
  );
}

function DravidaTemple({ cx, by, fill, accent }) {
  const tiers = [[22,9],[17,9],[12,9],[8,8],[5,8]];
  return (
    <g>
      <rect x={cx-24} y={by-8} width={48} height={8} rx={1.5} fill={fill} />
      {tiers.map(([hw, h], i) => (
        <rect key={i} x={cx-hw} y={by-17-(i*9)} width={hw*2} height={h} rx={1.5} fill={fill} opacity={.93-i*.04} />
      ))}
      {[-2.5,0,2.5].map(dx => (
        <g key={dx}>
          <ellipse cx={cx+dx*2.2} cy={by-60} rx={1.6} ry={2.4} fill={accent} opacity={.65} />
          <circle cx={cx+dx*2.2} cy={by-63} r={1.3} fill={accent} opacity={.55} />
        </g>
      ))}
    </g>
  );
}

function BengaliTemple({ cx, by, fill, accent }) {
  return (
    <g>
      <rect x={cx-20} y={by-8} width={40} height={8} rx={1.5} fill={fill} />
      <rect x={cx-17} y={by-24} width={34} height={17} rx={2} fill={fill} opacity={.9} />
      <path d={`M${cx-20},${by-24} Q${cx-13},${by-50} ${cx},${by-59} Q${cx+13},${by-50} ${cx+20},${by-24}`}
        fill={fill} opacity={.87} />
      <polygon points={`${cx-4},${by-57} ${cx+4},${by-57} ${cx+2.5},${by-70} ${cx-2.5},${by-70}`} fill={fill} opacity={.83} />
      <circle cx={cx} cy={by-73} r={2.8} fill={accent} opacity={.82} />
      <line x1={cx} y1={by-76} x2={cx} y2={by-82} stroke={accent} strokeWidth={1.3} />
    </g>
  );
}

function CaveTemple({ cx, by, fill, accent }) {
  return (
    <g>
      <path d={`M${cx-35},${by} Q${cx-22},${by-15} ${cx-15},${by-17} L${cx+15},${by-17} Q${cx+22},${by-15} ${cx+35},${by} Z`}
        fill={fill} opacity={.82} />
      <path d={`M${cx-15},${by-17} Q${cx-18},${by-56} ${cx},${by-63} Q${cx+18},${by-56} ${cx+15},${by-17} Z`}
        fill={fill} opacity={.9} />
      <ellipse cx={cx} cy={by-35} rx={8} ry={13} fill={accent} opacity={.12} />
      <circle cx={cx} cy={by-35} r={5} fill={accent} opacity={.1} />
      <path d={`M${cx-30},${by} L${cx-38},${by-10} L${cx-24},${by-15} L${cx-15},${by-17}`} fill={fill} opacity={.67} />
      <path d={`M${cx+30},${by} L${cx+38},${by-10} L${cx+24},${by-15} L${cx+15},${by-17}`} fill={fill} opacity={.67} />
    </g>
  );
}

function HillTemple({ cx, by, fill, accent }) {
  return (
    <g>
      <path d={`M${cx-44},${by} Q${cx-15},${by-35} ${cx},${by-44} Q${cx+15},${by-35} ${cx+44},${by} Z`}
        fill={fill} opacity={.78} />
      <rect x={cx-12} y={by-57} width={24} height={13} rx={1.5} fill={fill} />
      <polygon points={`${cx-10},${by-57} ${cx+10},${by-57} ${cx+5},${by-76} ${cx-5},${by-76}`} fill={fill} opacity={.92} />
      <polygon points={`${cx-5},${by-76} ${cx+5},${by-76} ${cx+2.5},${by-86} ${cx-2.5},${by-86}`} fill={fill} />
      <circle cx={cx} cy={by-89} r={2.4} fill={accent} opacity={.88} />
      <line x1={cx} y1={by-92} x2={cx} y2={by-99} stroke={accent} strokeWidth={1.3} />
    </g>
  );
}

const TEMPLES = [NagaraTemple, DravidaTemple, BengaliTemple, CaveTemple, HillTemple];

// ── Main component ───────────────────────────────────────────────────────────

export default function ShaktiPeethIllustration({ peethId = 1, state = '', country = 'India' }) {
  const id   = peethId;
  const pal  = P[(id - 1) % 8];
  const TComp = TEMPLES[(id - 1) % 5];
  const terrType = useMemo(() => getTerrainType(state, country), [state, country]);

  const W = 120, H = 160;
  const cx = 60, by = 118;
  const uid = `sp${id}`;

  // Vary speed slightly per peeth for organic feel
  const floatDur  = `${3.5 + (id % 5) * 0.38}s`;
  const pulseDur  = `${2.8 + (id % 4) * 0.32}s`;
  const twOff     = (id % 7) * 0.28;

  const terrPath = buildTerrainPath(terrType, W, H);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg"
      style={{ width:'100%', height:'100%', display:'block' }} aria-hidden="true">

      <defs>
        <linearGradient id={`${uid}-sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={pal.s1} />
          <stop offset="100%" stopColor={pal.s2} />
        </linearGradient>
        <radialGradient id={`${uid}-halo`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={pal.g} stopOpacity=".42" />
          <stop offset="65%"  stopColor={pal.g} stopOpacity=".10" />
          <stop offset="100%" stopColor={pal.g} stopOpacity="0"   />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect width={W} height={H} fill={`url(#${uid}-sky)`} />

      {/* Crescent moon */}
      <circle cx={W-17} cy={15} r={8.5}  fill={pal.m} opacity={.85} />
      <circle cx={W-13} cy={12} r={7}    fill={pal.s1} />
      <circle cx={W-17} cy={15} r={15}   fill={pal.m} opacity={.05} />

      {/* Stars */}
      {STARS.map(([sx, sy], i) => (
        <circle key={i} cx={sx} cy={sy} r={.85} fill={pal.a} opacity={.55}
          style={{
            animation:`peeth-twinkle ${(1.5+(i*0.32+twOff)%2.1).toFixed(1)}s ease-in-out ${((i*0.21+twOff*.5)%1.8).toFixed(2)}s infinite`
          }} />
      ))}

      {/* Terrain */}
      <path d={terrPath} fill={pal.t} />

      {/* Terrain detail */}
      {terrType === 'mountain' && (
        <>
          <path d={`M${W*.36},${H*.575-28} Q${W*.375},${H*.575-33} ${W*.39},${H*.575-28} Z`} fill="white" opacity={.55} />
          <path d={`M${W*.485},${H*.575-16} Q${W*.5},${H*.575-21} ${W*.515},${H*.575-16} Z`} fill="white" opacity={.45} />
        </>
      )}
      {terrType === 'river' && (
        <path d={`M${W*.08},${H*.585} Q${W*.38},${H*.578} ${W*.65},${H*.585} Q${W*.82},${H*.59} ${W*.94},${H*.583}`}
          fill="none" stroke={pal.a} strokeWidth={.75} opacity={.22} />
      )}
      {terrType === 'coastal' && (
        <path d={`M0,${H*.581} Q${W*.25},${H*.572} ${W*.5},${H*.581} Q${W*.75},${H*.59} ${W},${H*.581}`}
          fill="none" stroke="#87CEEB" strokeWidth={.75} opacity={.28} />
      )}

      {/* Divine halo */}
      <ellipse cx={cx} cy={by-42} rx={40} ry={50}
        fill={`url(#${uid}-halo)`}
        style={{ animation:`peeth-pulse ${pulseDur} ease-in-out infinite` }} />

      {/* Temple (floating animation) */}
      <g style={{
        animation: `peeth-float ${floatDur} ease-in-out infinite`,
        transformOrigin: `${cx}px ${by}px`,
      }}>
        <TComp cx={cx} by={by} fill={pal.t} accent={pal.a} />
      </g>

      {/* Rising particles */}
      {PARTS.map((p, i) => (
        <circle key={i} cx={cx+p.dx} cy={by-4} r={1.3} fill={pal.a}
          style={{ animation:`peeth-rise ${p.dur} ease-out ${p.delay} infinite` }} />
      ))}

      {/* Diya at base */}
      <g style={{ animation:`peeth-flame 1s ease-in-out infinite`, transformOrigin:`${cx}px ${by+4}px` }}>
        <ellipse cx={cx} cy={by+4} rx={5.5} ry={2.5} fill={pal.a} opacity={.65} />
        <path d={`M${cx-2.5},${by+4} Q${cx},${by-6} ${cx+2.5},${by+4} Q${cx},${by-2} ${cx-2.5},${by+4}`}
          fill={pal.a} opacity={.85} />
        <circle cx={cx} cy={by} r={5} fill={pal.a} opacity={.12} />
      </g>

      {/* Om symbol */}
      <text x={cx} y={30} textAnchor="middle"
        fontFamily="serif" fontSize={13} fill={pal.a} opacity={.62}
        style={{ animation:`peeth-pulse ${pulseDur} ease-in-out .5s infinite` }}>
        ॐ
      </text>

    </svg>
  );
}
